'use client';

import React from 'react';
import { cn } from '@/lib/utils';
import { Play } from 'lucide-react';
import { SectionHead } from './SectionHead';
import { getGoalMetadata, type GoalSlug } from '@/lib/aviado/goals';
import { formatSupplementName } from '@/lib/articles';
import {
  useAudio,
  type QueueTrack,
} from '@/lib/contexts/ListenQueueContext.shim';

/**
 * ListenByGoal — curated audio tiles that feed the ListenDock.
 *
 * Clicking "Play all" on a tile enqueues every audio article tagged with the
 * goal (in reverse-chrono order from the underlying articles array) and starts
 * the first track. The global dock takes over from there.
 *
 * Spec: plan item 5.7 ("your idea" — home-page section that deep-links into
 * dock with a pre-loaded queue). No direct visual spec in the prototype;
 * matches the card treatment elsewhere on the home page.
 */

export interface ListenByGoalBucket {
  slug: GoalSlug;
  count: number;
  totalSeconds: number;
  tracks: QueueTrack[];
}

export interface ListenByGoalProps {
  buckets: ListenByGoalBucket[];
}

export function ListenByGoal({ buckets }: ListenByGoalProps) {
  const { playTrack, addToQueue, currentTrack } = useAudio();

  if (buckets.length === 0) return null;

  // Anchor `id="listen-by-goal"` — target of the `/listen` 301 redirect.
  const handlePlayAll = (tracks: QueueTrack[]) => {
    if (tracks.length === 0) return;
    // Don't re-issue playTrack on the already-playing track — it would reset
    // the audio element and jerk playback back to 0:00.
    if (currentTrack?.id !== tracks[0].id) {
      playTrack(tracks[0]);
    }
    for (let i = 1; i < tracks.length; i++) addToQueue(tracks[i]);
  };

  return (
    <section id="listen-by-goal" className="max-w-container mx-auto px-10 pt-24 scroll-mt-20">
      <SectionHead
        title="Listen by"
        accent="health goal"
        meta={<span>Curated evidence reviews for the commute</span>}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {buckets.map(({ slug, count, tracks, totalSeconds }) => {
          const meta = getGoalMetadata(slug);
          if (!meta) return null;
          const minutes = Math.ceil(totalSeconds / 60);
          const isFeaturedQueue = count > tracks.length;
          return (
            <div
              key={slug}
              className={cn(
                'rounded-[14px] bg-white border border-border',
                'p-6 hover:border-border-strong transition-colors duration-fast'
              )}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3
                    className="font-heading font-semibold text-[16px] text-warm-navy m-0 mb-0.5"
                    style={{ fontVariationSettings: '"opsz" 18' }}
                  >
                    {meta.label}
                  </h3>
                  <p className="font-mono text-[11px] tracking-[0.02em] text-slate m-0">
                    {isFeaturedQueue ? `${tracks.length} featured` : `${count} articles`}
                    {minutes > 0 ? ` · ~${minutes} min` : ''}
                  </p>
                </div>
                <span className="text-[22px]" aria-hidden="true">
                  {meta.icon}
                </span>
              </div>
              <ul className="list-none p-0 m-0 space-y-1 mb-4">
                {tracks.slice(0, 3).map((a) => (
                  <li key={a.id} className="font-mono text-[11px] text-slate truncate">
                    {formatSupplementName(a.supplement)} — {a.title}
                  </li>
                ))}
                {count > 3 && (
                  <li className="font-mono text-[11px] text-slate">
                    +{count - 3} more in this goal
                  </li>
                )}
              </ul>
              <button
                type="button"
                onClick={() => handlePlayAll(tracks)}
                className={cn(
                  'inline-flex items-center gap-1.5',
                  'min-h-12 px-3.5 py-2 rounded-md',
                  'bg-aviado-teal/10 text-aviado-teal-text',
                  'font-ui text-[12px] font-medium cursor-pointer',
                  'hover:bg-aviado-teal/20 transition-colors duration-fast'
                )}
                >
                <Play className="w-[13px] h-[13px]" fill="currentColor" />
                {isFeaturedQueue ? 'Play featured' : 'Play all'}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default ListenByGoal;
